import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon,
  LockOpenIcon,
  KeyIcon,
} from "@heroicons/react/outline";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import fire from "../../utils/Toastr";

export default function PayrollOfficerDetails() {
  const { organizationId, payrollOfficerId } = useParams();
  const navigate = useNavigate();
  const [officer, setOfficer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (organizationId && payrollOfficerId) {
      fetchOfficerDetails();
    }
  }, [organizationId, payrollOfficerId]);

  const fetchOfficerDetails = async () => {
    setLoading(true);
    try {
      const response = await ThirdPartyServices.viewPayrollOfficer(
        organizationId,
        payrollOfficerId,
      );

      if (response.status && response.data) {
        setOfficer(response.data);
      } else {
        setError(response.message || "Payroll officer not found");
      }
    } catch (err) {
      setError("Failed to fetch payroll officer details");
      fire("error", "Failed to fetch payroll officer details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Loading..." current="thirdparty" pageTag="thirdparty">
        <div className="flex justify-center items-center h-64">
          <HollowDotsSpinner color="#4A90E2" />
        </div>
      </Layout>
    );
  }

  if (error || !officer) {
    return (
      <Layout
        title="Payroll Officer Not Found"
        current="thirdparty"
        pageTag="thirdparty"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Payroll Officer Not Found
            </h3>
            <p className="text-gray-500 mb-4">
              {error || "The requested payroll officer could not be found."}
            </p>
            <button
              onClick={() => navigate("/thirdparty/payroll-officers")}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Payroll Officers
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const InfoCard = ({ label, value, icon: Icon }) => (
    <div className="flex items-start">
      {Icon && <Icon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />}
      <div>
        <dt className="text-xs font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900">{value || "N/A"}</dd>
      </div>
    </div>
  );

  const SectionCard = ({ title, children, className = "" }) => (
    <div className={`bg-white shadow-sm border border-gray-200 rounded-lg ${className}`}>
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );

  const DetailRow = ({ label, value }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{value || "N/A"}</dd>
    </div>
  );

  return (
    <Layout
      title={`${officer.userName || "Payroll Officer"} - Details`}
      current="thirdparty"
      pageTag="thirdparty"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/thirdparty/payroll-officers"
          className="inline-flex items-center text-sm text-red-600 hover:text-red-500 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Payroll Officers
        </Link>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-red-600 to-red-800 px-8 py-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center">
                  <UserCircleIcon className="h-12 w-12 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    {officer.userName || "—"}
                  </h1>
                  <p className="text-red-100 mt-1">{officer.officerType}</p>
                  <div className="flex items-center space-x-4 mt-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        officer.isActive
                          ? "bg-green-400 text-white"
                          : "bg-gray-400 text-white"
                      }`}
                    >
                      {officer.isActive ? "Active" : "Inactive"}
                    </span>
                    {officer.isLocked && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-400 text-white">
                        Locked
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        officer.officerType === "Maker"
                          ? "bg-purple-400 text-white"
                          : "bg-orange-400 text-white"
                      }`}
                    >
                      {officer.officerType}
                    </span>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block">
                <button
                  onClick={() =>
                    navigate(
                      `/thirdparty/payroll-officers?organization=${organizationId}`,
                    )
                  }
                  className="inline-flex items-center px-4 py-2 border border-white/30 text-sm font-medium rounded-md shadow-sm text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back to List
                </button>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <SectionCard title="Officer Information">
                <dl className="divide-y divide-gray-200">
                  <DetailRow label="Username" value={officer.userName} />
                  <DetailRow label="Officer Type" value={officer.officerType} />
                  <DetailRow label="BVN" value={officer.bvn} />
                  <DetailRow label="Email Address" value={officer.emailAddress} />
                  <DetailRow label="Phone Number" value={officer.phoneNumber} />
                </dl>
              </SectionCard>

              {officer.bvnDetails && (
                <SectionCard title="BVN Details">
                  <dl className="divide-y divide-gray-200">
                    <DetailRow label="Full Name" value={`${officer.bvnDetails.firstName} ${officer.bvnDetails.middleName} ${officer.bvnDetails.lastName}`} />
                    <DetailRow label="Date of Birth" value={officer.bvnDetails.dateOfBirth} />
                    <DetailRow label="Phone Number 1" value={officer.bvnDetails.phoneNumber1} />
                    <DetailRow label="Phone Number 2" value={officer.bvnDetails.phoneNumber2} />
                    <DetailRow label="Email" value={officer.bvnDetails.email} />
                    <DetailRow label="Gender" value={officer.bvnDetails.gender} />
                    <DetailRow label="Title" value={officer.bvnDetails.title} />
                    <DetailRow label="Nationality" value={officer.bvnDetails.nationality} />
                    <DetailRow label="State of Origin" value={officer.bvnDetails.stateOfOrigin} />
                    <DetailRow label="State of Residence" value={officer.bvnDetails.stateOfResidence} />
                    <DetailRow label="LGA of Origin" value={officer.bvnDetails.lgaOfOrigin} />
                    <DetailRow label="LGA of Residence" value={officer.bvnDetails.lgaOfResidence} />
                    <DetailRow label="Residential Address" value={officer.bvnDetails.residentialAddress} />
                    <DetailRow label="Marital Status" value={officer.bvnDetails.maritalStatus} />
                    <DetailRow label="Level of Account" value={officer.bvnDetails.levelOfAccount} />
                    <DetailRow label="Name on Card" value={officer.bvnDetails.nameOnCard} />
                    <DetailRow label="NIN" value={officer.bvnDetails.nin} />
                    <DetailRow label="Watch Listed" value={officer.bvnDetails.watchListed} />
                    <DetailRow label="Enrollment Bank" value={officer.bvnDetails.enrollmentBank} />
                    <DetailRow label="Enrollment Branch" value={officer.bvnDetails.enrollmentBranch} />
                    <DetailRow label="Registration Date" value={officer.bvnDetails.registrationDate} />
                  </dl>
                </SectionCard>
              )}
            </div>

            <div className="space-y-6">
              <SectionCard title="Account Status">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {officer.isActive ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <span className="text-sm text-gray-700">Status</span>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        officer.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {officer.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {officer.isLocked ? (
                        <LockClosedIcon className="h-5 w-5 text-yellow-500 mr-2" />
                      ) : (
                        <LockOpenIcon className="h-5 w-5 text-green-500 mr-2" />
                      )}
                      <span className="text-sm text-gray-700">Locked</span>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        officer.isLocked
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {officer.isLocked ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <KeyIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">Password Retries</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {officer.passwordRetries || 0}
                    </span>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
